import { useCallback, useEffect, useMemo, useRef } from 'react';
import { NodeCreatableTarget } from './NodeCreatableTarget';
import { Rect } from '@view-model/models/common/basic/Rect';
import { useD3DblClickCallback } from '@view-model/models/common/hooks';
import { D3Mouse } from '@view-model/models/common/d3/D3Mouse';
import { HandleD3Drag } from '@view-model/models/common/d3/HandleD3Drag';
import type { D3DragEvent } from 'd3-drag';
import { selectAtom } from 'jotai/utils';
import { dropTargetViewAtom } from '@view-model/adapter/dropTargetViewAtom';
import { useAtomValue } from 'jotai';
import type { ViewId } from 'balus-common';
import type { ThemeColor } from '@view-model/models/common/color';
import { ViewColor } from '@view-model/ui/components/View/ViewStyle';
import type { GetGuardProps } from '@model-framework/drag-file/useDragFileGuard';
import type { Position, SizeType, PositionPair } from '@view-model/models/common/types/ui';
import { useD3RightClickCallback } from '@view-model/models/common/hooks/useD3RightClickCallback';
import type { MultiSelectionMode } from '@user/pages/ViewModelPage/MultiSelectionMode';

export type ViewSelectionDragStrategy = 'suppress' | 'threshold' | 'none';

// mapモードでのShift+ドラッグ時にクリックとドラッグを区別するための閾値（スクリーン座標、5px）
const CLICK_THRESHOLD_SQUARED = 25;

type Props = {
  viewId: ViewId;
  size: SizeType;
  onClickCanvas(e: MouseEvent): void;
  onRightClickCanvas(position: Position): void;
  onDblClickCanvas(position: Position): void;
  onRectSelection(rect: Rect): void;
  onRectSelectionEnd(rect: Rect): void;
  checkDraggingMultiSelectionDisabled: (e: MouseEvent) => boolean;
  onSelectSingleView(): void;
  viewThemeColor: ThemeColor;
  getGuardProps: GetGuardProps;
  hookDragCanvas: () => void;
  hookDragEndCanvas: () => void;
  multiSelectionMode: MultiSelectionMode;
  getDragStrategyForViewSelection: (e: MouseEvent) => ViewSelectionDragStrategy;
};

export const ModelCanvas: React.FC<Props> = ({
  viewId,
  size,
  onClickCanvas,
  onRightClickCanvas,
  onDblClickCanvas,
  onRectSelection,
  onRectSelectionEnd,
  checkDraggingMultiSelectionDisabled,
  onSelectSingleView,
  viewThemeColor,
  getGuardProps,
  hookDragCanvas,
  hookDragEndCanvas,
  multiSelectionMode,
  getDragStrategyForViewSelection,
}: Props) => {
  const myRef = useRef<SVGRectElement>(null);
  const pointsRef = useRef<PositionPair>({ start: null, end: null });
  const d3Mouse = useMemo(() => new D3Mouse(myRef), []);

  // ドラッグ戦略の状態管理用ref
  const dragStrategyRef = useRef<ViewSelectionDragStrategy>('none');
  const dragMovedRef = useRef(false);
  const committedToDragRef = useRef(false);
  const dragStartClientRef = useRef({ x: 0, y: 0 });

  useD3DblClickCallback(
    myRef,
    useCallback(
      (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        if (NodeCreatableTarget.isTarget(target)) {
          const [x, y] = d3Mouse.getPosition(event);
          onDblClickCanvas({ x, y });
        }
      },
      [d3Mouse, onDblClickCanvas]
    ),
    // 本来はハンドルする場合だけ伝搬を止めればいいが、現状ではズームが発動するのでstopPropagationで伝搬を止める
    true
  );

  useD3RightClickCallback(
    myRef,
    useCallback(
      (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        if (NodeCreatableTarget.isTarget(target)) {
          const [x, y] = d3Mouse.getPosition(event);
          onRightClickCanvas({ x, y });
        }
      },
      [d3Mouse, onRightClickCanvas]
    ),
    // 本来はハンドルする場合だけ伝搬を止めればいいが、現状ではズームが発動するのでstopPropagationで伝搬を止める
    true
  );

  useEffect(() => {
    const resetDragState = () => {
      dragStrategyRef.current = 'none';
      dragMovedRef.current = false;
      committedToDragRef.current = false;
      pointsRef.current = { start: null, end: null };
    };

    const onDragStart = function (event: D3DragEvent<Element, unknown, unknown>) {
      // 複数ビュー選択モード時はドラッグを抑制（マウス移動追跡用にdragMovedRefのみリセット）
      if (multiSelectionMode.isMultiViewsSelectionMode) {
        dragMovedRef.current = false;
        return;
      }

      const sourceEvent = event.sourceEvent as MouseEvent;
      dragStrategyRef.current = getDragStrategyForViewSelection(sourceEvent);
      dragMovedRef.current = false;
      committedToDragRef.current = false;
      dragStartClientRef.current = { x: sourceEvent.clientX, y: sourceEvent.clientY };

      // editモード + 修飾キー: 完全にドラッグ抑制（開始位置も記録しない）
      if (dragStrategyRef.current === 'suppress') {
        return;
      }

      const [x, y] = d3Mouse.getPosition(event);
      pointsRef.current.start = { x, y };
    };

    const onDrag = function (event: D3DragEvent<Element, unknown, unknown>) {
      if (multiSelectionMode.isMultiViewsSelectionMode) {
        dragMovedRef.current = true;
        return;
      }

      // editモード + 修飾キー: 完全にドラッグ抑制
      if (dragStrategyRef.current === 'suppress') {
        dragMovedRef.current = true;
        return;
      }

      const [x, y] = d3Mouse.getPosition(event);
      pointsRef.current.end = { x, y };

      // mapモード + 修飾キー: 閾値でクリック/ドラッグを判定
      if (dragStrategyRef.current === 'threshold' && !committedToDragRef.current) {
        const sourceEvent = event.sourceEvent as MouseEvent;
        const dx = sourceEvent.clientX - dragStartClientRef.current.x;
        const dy = sourceEvent.clientY - dragStartClientRef.current.y;
        if (dx * dx + dy * dy < CLICK_THRESHOLD_SQUARED) {
          dragMovedRef.current = true;
          return; // 閾値未満、待機
        }
        committedToDragRef.current = true;
      }

      // ビューのキャンバスクリックではonDragStartも発火するので
      // ビュー内でドラッグが行われた段階で要素の複数選択モードに移行
      onSelectSingleView();
      hookDragCanvas();

      const { start, end } = pointsRef.current;
      if (start && end) {
        onRectSelection(Rect.fromPositions([start, end]));
      }
    };

    const onDragEnd = function (event: D3DragEvent<Element, unknown, unknown>) {
      // 複数ビュー選択モード時のクリック/ドラッグ判定
      if (multiSelectionMode.isMultiViewsSelectionMode) {
        if (dragMovedRef.current) {
          // マウスが動いた場合: d3がnative clickを抑制するため、onClickCanvasを手動で呼び出す
          onClickCanvas(event.sourceEvent);
        }
        // マウスが動いていない場合: native clickが正常に発火するので何もしない
        dragMovedRef.current = false;
        pointsRef.current = { start: null, end: null };
        return;
      }

      const strategy = dragStrategyRef.current;

      // 抑制されたドラッグ or 閾値未満: d3がclickイベントを抑制しているため、onClickCanvasを手動で呼び出す
      if (
        (strategy === 'suppress' && dragMovedRef.current) ||
        (strategy === 'threshold' && !committedToDragRef.current && dragMovedRef.current)
      ) {
        onClickCanvas(event.sourceEvent);
        resetDragState();
        return;
      }

      // 通常のドラッグ終了（閾値超過含む）
      const { start, end } = pointsRef.current;
      if (start && end) {
        onRectSelectionEnd(Rect.fromPositions([start, end]));
        hookDragEndCanvas();
      }
      resetDragState();
    };

    // 複数ビュー選択モード時はドラッグによる要素の複数選択を抑止
    const handleD3Drag = new HandleD3Drag(myRef, checkDraggingMultiSelectionDisabled);
    handleD3Drag.onMounted(onDrag, onDragStart, onDragEnd);
    return () => {
      handleD3Drag.onWillUnmounted();
    };
  }, [
    size,
    onSelectSingleView,
    onRectSelectionEnd,
    d3Mouse,
    onRectSelection,
    checkDraggingMultiSelectionDisabled,
    hookDragCanvas,
    hookDragEndCanvas,
    multiSelectionMode,
    getDragStrategyForViewSelection,
    onClickCanvas,
  ]);

  const isDropTargetAtom = useMemo(
    () =>
      selectAtom(
        dropTargetViewAtom,
        (dropTarget) => viewId === dropTarget?.viewId && dropTarget.stickyZoneIds.length === 0
      ),
    [viewId]
  );
  const isDropTarget = useAtomValue(isDropTargetAtom);

  const color = useMemo(() => ViewColor.getFillColor(viewThemeColor), [viewThemeColor]);

  return (
    <g width={size.width} height={size.height}>
      {/* 土台となるキャンバス */}
      <rect
        id={viewId}
        className={NodeCreatableTarget.targetClassName}
        ref={myRef}
        width={size.width}
        height={size.height}
        rx={64}
        ry={64}
        onClick={(e) => onClickCanvas(e.nativeEvent)}
        {...getGuardProps()}
        fill={isDropTarget ? '#f5f5f5' : color}
      />
    </g>
  );
};
